import { useEffect, useState } from "react";
import axios from "axios";
import { UserPlus, MailCheck, LogIn, ShoppingBag, Mail, StickyNote, BookOpen, CheckCircle2, GraduationCap, Trophy, LifeBuoy, Inbox, Send, Circle } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  joined: UserPlus, verified: MailCheck, login: LogIn, purchase: ShoppingBag, email: Mail, note: StickyNote, story: BookOpen,
  module: CheckCircle2, graduation: GraduationCap, wall: Trophy, grace: LifeBuoy, received: Inbox, reply: Send,
};
const TONE = {
  purchase: "text-emerald-400 border-emerald-400/50", note: "text-[#B45309] border-[#F59E0B]/60", graduation: "text-emerald-300 border-emerald-300/60",
  wall: "text-emerald-300 border-emerald-300/60", reply: "text-[#B45309] border-[#F59E0B]/60", received: "text-[#B45309] border-[#F59E0B]/60",
};
const when = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };

export const TimelineList = ({ targetType, targetId, testIdPrefix = "timeline" }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setData(null);
    setError(false);
    axios.get(`${API}/admin/timeline`, { params: { target_type: targetType, target_id: targetId } })
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [targetType, targetId]);

  if (error) return <p className="text-xs text-red-300" data-testid={`${testIdPrefix}-error`}>Couldn't load the history.</p>;
  if (!data) return <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] animate-pulse" data-testid={`${testIdPrefix}-loading`}>Loading history…</p>;

  return (
    <ol className="relative border-l border-[#E7DFD5] ml-3 space-y-5" data-testid={`${testIdPrefix}-list`}>
      {data.events.length === 0 && <li className="pl-6 text-xs text-[#555]" data-testid={`${testIdPrefix}-empty`}>Nothing recorded yet.</li>}
      {data.events.map((e, i) => {
        const Icon = ICONS[e.kind] || Circle;
        const tone = TONE[e.kind] || "text-[#78716C] border-[#E7DFD5]";
        return (
          <li key={`${e.kind}-${e.at}-${i}`} className="relative pl-7" data-testid={`${testIdPrefix}-event-${i}`} data-kind={e.kind}>
            <span className={`absolute -left-[13px] top-0.5 w-6 h-6 rounded-full bg-[#FFFFFF] border flex items-center justify-center ${tone}`}>
              <Icon size={12} />
            </span>
            <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#A8A29E]">{when(e.at)}{e.status && e.status !== "sent" ? ` · ${e.status}` : ""}</div>
            <div className={`text-sm mt-0.5 ${e.refunded ? "text-red-300 line-through" : "text-[#1C1917]"}`}>{e.title}</div>
            {e.detail && <div className="text-xs text-[#78716C] mt-0.5 leading-relaxed whitespace-pre-wrap break-words">{e.detail}{e.by ? <span className="text-[#555]"> · {e.by}</span> : null}</div>}
          </li>
        );
      })}
    </ol>
  );
};

export const MemberTimelineSheet = ({ member, onClose }) => (
  <Sheet open={Boolean(member)} onOpenChange={(o) => !o && onClose()}>
    <SheetContent side="right" className="bg-[#FFFFFF] border-l border-[#F59E0B]/40 text-[#1C1917] w-full sm:max-w-lg overflow-y-auto" data-testid="member-timeline-sheet">
      {member && (
        <>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#B45309] mb-1">Member · history</div>
          <SheetTitle className="font-display text-3xl tracking-tight leading-none text-[#1C1917]" data-testid="member-timeline-name">{member.name || member.email}</SheetTitle>
          <SheetDescription className="text-[#78716C] text-sm">{member.email} · {member.role === "admin" ? "admin" : `${member.streak_current}-day streak`}{member.entitlements?.filter((e) => e !== "modules").length ? ` · ${member.entitlements.filter((e) => e !== "modules").join(", ")}` : ""}</SheetDescription>
          <div className="mt-6">
            <TimelineList targetType="member" targetId={member.id} testIdPrefix="member-timeline" />
          </div>
        </>
      )}
    </SheetContent>
  </Sheet>
);
